import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu, X } from "lucide-react";
import { toast } from "sonner";

import { Container } from "@/platform/layout/container";
import { ThemeToggle } from "@/platform/layout/theme-toggle";
import { Button } from "@/platform/ui/button";
import { cn } from "@/platform/lib/cn";
import { useSession } from "@/platform/auth/use-session";
import { useProfile } from "@/platform/auth/use-profile";
import { signOut } from "@/platform/auth/api";
import { useTenant } from "@/platform/tenants/use-tenant";

const publicNav = [
  { to: "/", label: "Visão" },
  { to: "/metodo", label: "Método" },
  { to: "/precos", label: "Planos" },
];

const einsteinAuthedNav = [
  { to: "/painel", label: "Painel" },
  { to: "/plano", label: "Plano" },
  { to: "/revisar", label: "Revisar" },
  { to: "/redacao", label: "Redação" },
  { to: "/discursivas", label: "Discursivas" },
  { to: "/simulador", label: "Simulado" },
  { to: "/evolucao", label: "Evolução" },
];

const uspAuthedNav = [
  { to: "/usp/painel", label: "Painel" },
  { to: "/usp/plano", label: "Plano" },
  { to: "/usp/revisar", label: "Revisar" },
  { to: "/usp/redacao", label: "Redação" },
  { to: "/usp/2afase", label: "2ª fase" },
  { to: "/usp/simulador", label: "Simulado" },
  { to: "/usp/evolucao", label: "Evolução" },
];

export function SiteHeader() {
  const { isAuthenticated } = useSession();
  const { profile } = useProfile();
  const tenant = useTenant();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const authedNav = tenant?.slug === "usp" ? uspAuthedNav : einsteinAuthedNav;
  const baseNav = isAuthenticated ? authedNav : publicNav;
  const nav = profile?.is_admin ? [...baseNav, { to: "/admin", label: "Admin" }] : baseNav;
  const homeTo = isAuthenticated ? (tenant?.slug === "usp" ? "/usp/painel" : "/painel") : "/";

  // Fecha o drawer ao trocar de rota.
  useEffect(() => setMobileOpen(false), [location.pathname]);

  // Trava o scroll do body quando o drawer está aberto (mobile).
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  return (
    <header className="sticky top-0 z-40 border-b border-[--color-border] bg-[color:var(--color-background)]/85 backdrop-blur-md">
      <Container size="wide">
        <div className="flex h-14 items-center justify-between gap-6">
          <Link
            to={homeTo}
            className="group flex items-center gap-2.5"
          >
            <span
              aria-hidden
              className="grid size-6 place-items-center rounded-xs bg-[color:var(--color-foreground)] text-[color:var(--color-background)] text-[9px] font-bold leading-none tracking-tight transition-colors group-hover:bg-[color:var(--color-accent)]"
            >
              NS
            </span>
            <span className="flex items-baseline gap-1.5 font-display text-[length:var(--text-body-sm)] font-semibold tracking-[-0.01em]">
              Neuronix
              <span className="text-[color:var(--color-muted-foreground)] font-normal text-[length:var(--text-caption)]">
                Study
              </span>
            </span>
          </Link>

          {tenant && (
            <span
              aria-label={`Produto ativo: ${tenant.name}`}
              className="hidden items-center gap-1.5 rounded-full border border-[--color-border] px-2.5 py-0.5 text-[length:var(--text-caption)] font-medium text-[color:var(--color-muted-foreground)] md:inline-flex"
            >
              <span
                aria-hidden
                className="size-1.5 rounded-full"
                style={{ backgroundColor: `hsl(${tenant.accent.hsl})` }}
              />
              {tenant.shortName}
            </span>
          )}

          <nav aria-label="Principal" className="hidden md:flex items-center gap-0.5">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  cn(
                    "relative rounded-xs px-2.5 py-1.5 text-[length:var(--text-body-sm)] transition-colors duration-180",
                    isActive
                      ? "text-[color:var(--color-foreground)]"
                      : "text-[color:var(--color-muted-foreground)] hover:text-[color:var(--color-foreground)]",
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    {isActive ? (
                      <span
                        aria-hidden
                        className="absolute inset-x-2.5 -bottom-[15px] h-px bg-[color:var(--color-foreground)]"
                      />
                    ) : null}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <ThemeToggle />
            {isAuthenticated ? <UserMenu /> : <GuestActions />}
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label="Abrir menu"
              aria-expanded={mobileOpen}
              className="md:hidden grid h-11 w-11 place-items-center rounded-xs text-[color:var(--color-foreground)] hover:bg-[color:var(--color-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-ring)]"
            >
              <Menu className="size-5" aria-hidden />
            </button>
          </div>
        </div>
      </Container>

      <MobileDrawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        nav={nav}
        isAuthenticated={isAuthenticated}
        tenantLabel={tenant?.shortName ?? null}
        tenantAccent={tenant?.accent.hsl ?? null}
      />
    </header>
  );
}

function MobileDrawer({
  open,
  onClose,
  nav,
  isAuthenticated,
  tenantLabel,
  tenantAccent,
}: {
  open: boolean;
  onClose: () => void;
  nav: Array<{ to: string; label: string }>;
  isAuthenticated: boolean;
  tenantLabel: string | null;
  tenantAccent: string | null;
}) {
  const navigate = useNavigate();

  async function handleSignOut() {
    try {
      await signOut();
      onClose();
      toast.success("Sessão encerrada.");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Não foi possível sair.");
    }
  }

  if (!open) return null;

  return (
    <div className="md:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Menu">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden
      />
      <div className="absolute inset-y-0 right-0 flex w-[88vw] max-w-sm flex-col bg-[color:var(--color-background)] shadow-2xl">
        <div className="flex h-14 items-center justify-between border-b border-[--color-border] px-5">
          {tenantLabel ? (
            <span className="inline-flex items-center gap-1.5 text-[length:var(--text-body-sm)] font-semibold">
              {tenantAccent ? (
                <span
                  aria-hidden
                  className="size-2 rounded-full"
                  style={{ backgroundColor: `hsl(${tenantAccent})` }}
                />
              ) : null}
              {tenantLabel}
            </span>
          ) : (
            <span className="font-display text-[length:var(--text-body-sm)] font-semibold">Menu</span>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar menu"
            className="grid h-11 w-11 place-items-center rounded-xs text-[color:var(--color-foreground)] hover:bg-[color:var(--color-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-ring)]"
          >
            <X className="size-5" aria-hidden />
          </button>
        </div>

        <nav aria-label="Principal mobile" className="flex-1 overflow-y-auto px-3 py-4">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                cn(
                  "block rounded-lg px-3 py-3 text-[length:var(--text-body)] font-medium min-h-11",
                  isActive
                    ? "bg-[color:var(--color-muted)] text-[color:var(--color-foreground)]"
                    : "text-[color:var(--color-muted-foreground)] hover:bg-[color:var(--color-muted)]/60 hover:text-[color:var(--color-foreground)]",
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {isAuthenticated && (
          <div className="border-t border-[--color-border] p-4">
            <button
              type="button"
              onClick={handleSignOut}
              className="flex w-full items-center justify-center gap-2 rounded-lg px-3 py-3 min-h-11 text-[length:var(--text-body-sm)] font-medium text-[color:var(--color-muted-foreground)] hover:bg-[color:var(--color-muted)] hover:text-[color:var(--color-foreground)]"
            >
              <LogOut className="size-4" aria-hidden />
              Sair
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function GuestActions() {
  return (
    <>
      <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex">
        <Link to="/entrar">Entrar</Link>
      </Button>
      <Button asChild size="sm">
        <Link to="/cadastro">Começar</Link>
      </Button>
    </>
  );
}

function UserMenu() {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const name = profile?.display_name ?? profile?.full_name?.split(" ")[0] ?? "Conta";
  const initials = name
    .split(" ")
    .map((p) => p[0]?.toUpperCase())
    .filter(Boolean)
    .slice(0, 2)
    .join("");

  async function handleSignOut() {
    try {
      await signOut();
      toast.success("Sessão encerrada.");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Não foi possível sair.");
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Link
        to="/conta"
        aria-label="Conta"
        className="hidden h-8 select-none items-center gap-2 rounded-full border border-[--color-border] bg-[color:var(--color-card)] pl-1 pr-3 text-[length:var(--text-caption)] font-medium text-[color:var(--color-foreground)] transition-colors duration-150 ease-out hover:border-[--color-accent]/40 sm:inline-flex"
      >
        <span className="grid size-6 place-items-center rounded-full bg-[color:var(--color-accent)] text-[10px] font-bold text-white">
          {initials || "·"}
        </span>
        {name}
      </Link>
      <button
        type="button"
        onClick={handleSignOut}
        aria-label="Sair"
        className="hidden sm:grid h-11 w-11 place-items-center rounded-full text-[color:var(--color-muted-foreground)] transition-colors duration-150 ease-out hover:bg-[color:var(--color-muted)] hover:text-[color:var(--color-foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-ring)]"
      >
        <LogOut className="size-4" aria-hidden />
      </button>
    </div>
  );
}
